import moment from 'moment';
import * as Sentry from '@sentry/react';
import { createAction } from 'redux-act';
import { paramsForServer } from 'feathers-hooks-common';
import { has, get, isEmpty } from 'lodash';
import { showNotification } from 'utils/appUtils';
//
import { PAGINATION_TYPES, PAGINATION_LIMIT, PAGINATION_LIMIT_SHORT, KEYS } from 'utils/constants';
import {
  LessonService,
  BookingOptionService,
  UserService,
  TopicService,
  OrganizationService,
  InstructorWorkLogService,
  AvatarSetService,
} from 'services';
import { setLoading } from '../loading/actions';
import { makeApprovedAtQuery } from './utils/challenges';
import { showErrorNotification } from '../../utils/error-handlers';

export const pagePaginationSuccess = createAction('pagePaginationSuccess');
export const updatePaginationItem = createAction('updatePaginationItem');
export const deletePaginationItem = createAction('deletePaginationItem');
export const addPaginationItem = createAction('addPaginationItem');
export const toggleGlobalItem = createAction('toggleGlobalItem');
export const fetchStudentPreferenceInstructorSuccess = createAction(
  'fetchStudentPreferenceInstructorSuccess'
);

export const fetchBookingOptions = (page = 1) => {
  return async (dispatch) => {
    const key = PAGINATION_TYPES.bookingOptions;
    const loadingObj = {
      key,
      value: true,
    };

    try {
      dispatch(setLoading(loadingObj));
      const res = await BookingOptionService.find({
        query: {
          $limit: PAGINATION_LIMIT,
          $skip: (page - 1) * PAGINATION_LIMIT,
        },
        populate: 'DEFAULT',
      });

      dispatch(
        pagePaginationSuccess({
          data: res,
          key,
        })
      );
    } catch (e) {
      showNotification('error');
    }

    loadingObj.value = false;
    dispatch(setLoading(loadingObj));
  };
};

export const addBookingOption = (payload) => {
  return async (dispatch) => {
    const key = PAGINATION_TYPES.bookingOptions;
    try {
      const res = await BookingOptionService.create(payload);
      dispatch(
        addPaginationItem({
          value: res,
          key,
        })
      );
    } catch (e) {
      showNotification('error');
    }
  };
};

export const updateBookingOption = (payload) => {
  return async (dispatch) => {
    const key = PAGINATION_TYPES.bookingOptions;
    try {
      const data = await BookingOptionService.patch(
        payload._id,
        payload,
        paramsForServer({
          action: 'update-bookable-times',
        })
      );

      dispatch(
        updatePaginationItem({
          id: payload._id,
          data,
          key,
        })
      );
    } catch (e) {
      showNotification('error');
    }
  };
};

export const deleteBookingOption = (id) => {
  return async (dispatch) => {
    const key = PAGINATION_TYPES.bookingOptions;
    try {
      await BookingOptionService.remove(id);
      dispatch(
        deletePaginationItem({
          key: key,
          value: id,
        })
      );
    } catch (e) {
      showNotification('error');
    }
  };
};

export const toggleDefaultBookingOption = (id) => {
  return async (dispatch) => {
    const key = PAGINATION_TYPES.bookingOptions;
    try {
      await BookingOptionService.patch(id, {
        isDefault: true,
        action: 'update-default-booking-options',
      });

      dispatch(
        toggleGlobalItem({
          key: key,
          id,
          data: {
            key: 'isDefault',
            value: true,
            oldValue: false,
          },
        })
      );
    } catch (e) {
      showNotification('error');
    }
  };
};

export const fetchPreferenceInstructor = (instructors) => {
  return async (dispatch) => {
    dispatch(
      setLoading({
        key: KEYS.fetchStudentPreferenceInstructor,
        value: true,
      })
    );

    try {
      const { preferredInstructors, dislikedInstructors } = instructors || {};

      const [res1, res2] = await Promise.all([
        Array.isArray(preferredInstructors) &&
          preferredInstructors.length > 0 &&
          UserService.find({
            query: {
              _id: { $in: preferredInstructors },
            },
          }),
        Array.isArray(dislikedInstructors) &&
          dislikedInstructors.length > 0 &&
          UserService.find({
            query: {
              _id: { $in: dislikedInstructors },
            },
          }),
      ]);

      const result = { preferredInstructors: res1.data, dislikedInstructors: res2.data };
      dispatch(fetchStudentPreferenceInstructorSuccess(result));
    } catch (err) {
      showNotification('error');
    } finally {
      dispatch(setLoading({ key: KEYS.fetchStudentPreferenceInstructor, value: false }));
    }
  };
};

export const updateUserInPage = (payload) => {
  return async (dispatch) => {
    const key = PAGINATION_TYPES.users;
    let res;
    if (!payload._id) {
      return;
    } else {
      dispatch(
        updatePaginationItem({
          key,
          id: payload._id,
          data: {
            name: res.name,
            email: res.email,
            phoneNumber: res.phoneNumber,
          },
        })
      );
    }

    return res;
  };
};

export function fetchInstructorReviews(instructorReviewFilter, page) {
  const { search, learningSubject } = instructorReviewFilter;
  return async (dispatch) => {
    const key = PAGINATION_TYPES.instructorReviews;
    dispatch(
      setLoading({
        key,
        value: true,
      })
    );

    try {
      let otherQuery = {};
      if (search) {
        otherQuery['$or'] = [
          {
            customerId: search,
          },
          {
            instructorId: search,
          },
        ];
      }

      const data = await LessonService.find({
        query: {
          $sort: { start: -1 },
          $limit: PAGINATION_LIMIT,
          $skip: (page - 1) * PAGINATION_LIMIT,
          instructorStudentFeedback: { $exists: true },
          learningSubject,
          ...otherQuery,
        },
      });

      dispatch(
        pagePaginationSuccess({
          data,
          key,
        })
      );
    } finally {
      dispatch(
        setLoading({
          key,
          value: false,
        })
      );
    }
  };
}

export function fetchOrganizations(search) {
  return async (dispatch) => {
    const key = PAGINATION_TYPES.organizations;
    dispatch(
      setLoading({
        key,
        value: true,
      })
    );

    try {
      let otherQuery = {};
      if (search) {
        otherQuery['_id'] = search;
      }

      const data = await OrganizationService.find(
        paramsForServer({
          query: {
            $sort: { createdAt: '-1' },
            ...otherQuery,
          },
        })
      );

      dispatch(
        pagePaginationSuccess({
          data: { data, total: data.length },
          key,
        })
      );
    } catch (err) {
      showNotification('error', 'Something went wrong.');
    } finally {
      dispatch(
        setLoading({
          key,
          value: false,
        })
      );
    }
  };
}

export const createOrUpdateOrg = (payload) => {
  return async (dispatch) => {
    const key = PAGINATION_TYPES.organizations;
    let res;
    if (!payload._id) {
      res = await OrganizationService.create(payload);

      dispatch(
        addPaginationItem({
          value: res,
          key,
        })
      );
    } else {
      res = await OrganizationService.patch(payload._id, payload);
      const {
        name,
        code,
        logo,
        visibleContentTagIds,
        profileQuestionnaireUpdateTime,
        settings,
        isDemo,
        type,
      } = res;

      dispatch(
        updatePaginationItem({
          key,
          id: payload._id,
          data: {
            name,
            code,
            logo,
            visibleContentTagIds,
            profileQuestionnaireUpdateTime,
            settings,
            isDemo,
            type,
          },
        })
      );
    }

    return res;
  };
};

export const fetchTopics = (page, params) => {
  return async (dispatch) => {
    const {
      search = '',
      sorter,
      filters,
      options,
      contentTagIds,
      qaFilter,
      supportedLanguage,
      contentCreatedForOrgIds,
      meeoppLevels,
      onlyUndefinedMeeoppLevel,
      meeoppThemeContentTagIds,
      meeoppSubtopicIds,
      onlyEmptyMeeoppSubtopic,
      showArchivedOnly,
      showToLibraryStatus,
      learningSubjects,
    } = params;

    let $sort = { createdAt: -1 };
    if (has(sorter, 'order')) {
      $sort = { [sorter.field]: sorter.order === 'ascend' ? 1 : -1 };
    }

    let filterParams = {};
    if (!isEmpty(filters)) {
      const keys = Object.keys(filters);
      keys.forEach((key) => {
        if (!isEmpty(filters[key])) {
          filterParams[key] = { $in: filters[key] };
        }
      });
    }

    if (supportedLanguage) {
      filterParams.supportedLanguage = supportedLanguage;
    }

    if (contentCreatedForOrgIds) {
      filterParams.contentCreatedForOrgIds = { $all: contentCreatedForOrgIds };
    }

    const key = PAGINATION_TYPES.topics;
    const loadingObj = {
      key,
      value: true,
    };

    const approvedAtQuery = makeApprovedAtQuery(qaFilter);

    try {
      dispatch(setLoading(loadingObj));
      const paginateLimit = get(options, 'paginateShort')
        ? PAGINATION_LIMIT_SHORT
        : PAGINATION_LIMIT;

      const res = await TopicService.find(
        paramsForServer({
          query: {
            $limit: paginateLimit,
            $skip: (page - 1) * paginateLimit,
            $sort,
            contentTagIds: {
              $all: contentTagIds,
              ...(meeoppThemeContentTagIds && { $in: meeoppThemeContentTagIds }),
            },
            ...(!isEmpty(learningSubjects) && {
              learningSubject: { $in: learningSubjects },
            }),
            ...(showArchivedOnly ? { archivedAt: { $ne: null } } : { archivedAt: null }),
            ...(showToLibraryStatus && { showToLibraryStatus }),
            meeoppLevels,
            onlyUndefinedMeeoppLevel,
            onlyEmptyMeeoppSubtopic,
            meeoppSubtopicIds,
            ...filterParams, // Table filter overwrites tag dropdown
            ...approvedAtQuery,
          },
          populate: 'DEFAULT',
          action: 'fetch-topics',
          search,
        })
      );

      dispatch(
        pagePaginationSuccess({
          data: res,
          key,
        })
      );
    } catch (e) {
      Sentry.captureException(e);
    } finally {
      loadingObj.value = false;
      dispatch(setLoading(loadingObj));
    }
  };
};

export const fetchTopicById = (_id) => {
  return async (dispatch) => {
    const key = PAGINATION_TYPES.topics;
    try {
      dispatch(
        setLoading({
          key,
          value: true,
        })
      );

      // Using 'find' to return the same result object format
      const res = await TopicService.find(
        paramsForServer({
          query: {
            _id,
          },
          populate: 'DEFAULT',
          action: 'fetch-topics',
        })
      );

      dispatch(
        pagePaginationSuccess({
          data: res,
          key,
        })
      );
    } catch (e) {
      showErrorNotification(e.message);
    } finally {
      dispatch(
        setLoading({
          key,
          value: false,
        })
      );
    }
  };
};

export const fetchInstructorsWorkLogs = (searchParams) => {
  return async (dispatch) => {
    const { page, start, userId, sorter, displayZeroPaidMin, learningSubject } = searchParams;
    const key = PAGINATION_TYPES.instructorsWorkLogs;
    const loadingObj = { key, value: true };
    try {
      dispatch(setLoading(loadingObj));
      const otherArgs = {};
      if (start) {
        otherArgs['lessonSnapshot.start'] = start;
      }

      if (!displayZeroPaidMin) {
        otherArgs.paidMinutes = { $ne: 0 };
      }

      if (learningSubject) {
        otherArgs['lessonSnapshot.learningSubject'] = learningSubject;
      }

      if (userId) {
        otherArgs['$or'] = [
          {
            customerId: userId,
          },
          {
            instructorId: userId,
          },
        ];
      }

      const query = {
        $limit: PAGINATION_LIMIT,
        $skip: (page - 1) * PAGINATION_LIMIT,
        $sort: isEmpty(sorter) ? { 'lessonSnapshot.start': -1 } : sorter,
        ...otherArgs,
      };

      const res = await InstructorWorkLogService.find({
        query,
        populate: 'DEFAULT',
      });

      dispatch(
        pagePaginationSuccess({
          data: res,
          key,
        })
      );
    } catch (e) {
      showNotification('error', 'Something went wrong.');
    }

    loadingObj.value = false;
    dispatch(setLoading(loadingObj));
  };
};

export const fetchInstructorsMonthlyReport = (
  selectedYear,
  selectedMonth,
  instructorId,
  displayZeroPaidMinutes
) => {
  return async (dispatch) => {
    const key = PAGINATION_TYPES.fetchInstructorsMonthlyReport;
    dispatch(
      setLoading({
        key,
        value: true,
      })
    );

    const limitStart = moment({ year: selectedYear, month: selectedMonth })
      .subtract(1, 'months')
      .startOf('month')
      .format();

    const limitEnd = moment({ year: selectedYear, month: selectedMonth }).endOf('month').format();

    try {
      const query = {
        createdAt: { $gte: limitStart, $lte: limitEnd },
        updatedAt: { $gte: limitStart, $lte: limitEnd },
        instructorId,
        displayZeroPaidMinutes,
      };

      const data = await InstructorWorkLogService.find(
        paramsForServer({
          query,
          year: selectedYear,
          month: selectedMonth,
          action: 'monthly-worklog',
        })
      );

      dispatch(
        pagePaginationSuccess({
          data,
          key,
        })
      );
    } catch (e) {
      showNotification('error', 'Something went wrong.');
    } finally {
      dispatch(
        setLoading({
          key,
          value: false,
        })
      );
    }
  };
};

export const fetchStudentsInstructorPreference = (instructorId, page = 1, likesInstructor) => {
  return async (dispatch) => {
    const preference = likesInstructor
      ? 'studentsThatLikeInstructor'
      : 'studentsThatDislikeInstructor';

    const key = PAGINATION_TYPES[preference];

    dispatch(
      setLoading({
        key,
        value: true,
      })
    );

    try {
      const query = {
        [`instructorPreferences.${
          likesInstructor ? 'preferredInstructors' : 'dislikedInstructors'
        }`]: {
          $in: [instructorId],
        },
        $limit: PAGINATION_LIMIT,
        $skip: (page - 1) * PAGINATION_LIMIT,
        $sort: { createdAt: -1 },
      };

      const data = await UserService.find(
        paramsForServer({
          query,
        })
      );

      dispatch(
        pagePaginationSuccess({
          data,
          key,
        })
      );
    } catch (e) {
      showNotification('error', 'Something went wrong.');
    } finally {
      dispatch(
        setLoading({
          key,
          value: false,
        })
      );
    }
  };
};

// TODO: Points Query copy from this basically lmao

export const fetchAvatarSets = (page) => {
  return async (dispatch) => {
    const key = PAGINATION_TYPES.avatarSets; // setup redux
    const loadingObj = {
      key,
      value: true,
    };

    try {
      dispatch(setLoading(loadingObj));

      const res = await AvatarSetService.find(
        paramsForServer({
          query: {
            $limit: PAGINATION_LIMIT,
            $skip: (page - 1) * PAGINATION_LIMIT,
            $sort: { createdAt: -1 },
          },
          joins: {
            avatars: true,
          },
        })
      );

      dispatch(
        pagePaginationSuccess({
          data: res,
          key,
        })
      );
    } catch (e) {
      showNotification('error');
    } finally {
      loadingObj.value = false;
      dispatch(setLoading(loadingObj));
    }
  };
};

export const createAvatarSet = (payload, assets) => {
  return async (dispatch) => {
    const key = PAGINATION_TYPES.avatarSets;
    try {
      const res = await AvatarSetService.create(payload);
      dispatch(
        addPaginationItem({
          value: { ...assets, ...res },
          key,
        })
      );
    } catch (e) {
      showNotification('error');
    }
  };
};

export const updateAvatarSet = (payload, assets) => {
  return async (dispatch) => {
    const key = PAGINATION_TYPES.avatarSets;
    try {
      const data = await AvatarSetService.patch(payload._id, payload);
      dispatch(
        updatePaginationItem({
          id: payload._id,
          data: { ...assets, data },
          key,
        })
      );
    } catch (e) {
      showNotification('error');
    }
  };
};

export const deleteAvatarSet = (id) => {
  return async (dispatch) => {
    const key = PAGINATION_TYPES.avatarSets;
    try {
      await AvatarSetService.remove(id);
      dispatch(
        deletePaginationItem({
          key: key,
          value: id,
        })
      );
    } catch (e) {
      showNotification('error');
    }
  };
};
